import React, { useState, useRef, useEffect } from 'react';
import { Printer, Plus, Edit2, Save, X, Trash2, FileText } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

function HighwayTehsildar() {
    const [records, setRecords] = useState([]);
    const [tempData, setTempData] = useState({});
    const [formData, setFormData] = useState({
        bhusampadan_no: '',
        village_name: '',
        taluka: '',
        district: 'बुलढाणा'
    });
    const [recordTypeOptions, setRecordTypeOptions] = useState([]);
    const [projectId, setProjectId] = useState(null);
    const [proposalId, setProposalId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingRecords, setLoadingRecords] = useState(false);
    const [workSummary, setWorkSummary] = useState([]);
    const [reportData, setReportData] = useState([]);
    const { language, translations } = useLanguage();

    const ROLES = {
        COLLECTOR: 1,
        LAO: 3,
        SAMANVAY: 6,
        PROJECT_INCHARGE: 18,
        DATA_ENTRY: 14
    };

    const userData = JSON.parse(localStorage.getItem('userData'));
    const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

    const documentRef = useRef(null);

    // Fetch projectId similar to Section15
    useEffect(() => {
        const getProjectInfo = () => {
            const currentProjectData = localStorage.getItem('currentProjectData');
            if (currentProjectData) {
                try {
                    const projectData = JSON.parse(currentProjectData);
                    return {
                        id: projectData.project_id || projectData.id,
                        name: projectData.project_name || projectData.name
                    };
                } catch (e) {
                    console.error('Error parsing current project data:', e);
                }
            }

            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                try {
                    const project = JSON.parse(selectedProject);
                    return {
                        id: project.project_id || project.id,
                        name: project.project_name || project.name
                    };
                } catch (e) {
                    console.error('Error parsing selected project:', e);
                }
            }

            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    const projectId = user.data?.project_id || user.project_id;
                    const projectName = user.data?.project_name || user.project_name || 'Unknown Project';
                    return {
                        id: projectId,
                        name: projectName
                    };
                } catch (e) {
                    console.error('Error parsing user data:', e);
                }
            }
            return { id: null, name: null };
        };

        const projectInfo = getProjectInfo();
        setProjectId(projectInfo.id);
        console.log('Current Project Info:', projectInfo);
    }, []);

    // Fetch proposalId similar to Section15
    useEffect(() => {
        const getProposalInfo = () => {
            const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');

            if (currentProposal) {
                try {
                    return {
                        proposalId: currentProposal?.data?.default_proposal ||
                            currentProposal?.data?.proposal_id ||
                            currentProposal?.default_proposal ||
                            currentProposal?.proposal_id ||
                            null
                    }
                } catch (e) {
                    console.error('Error parsing proposal data:', e);
                }
            }
            return { proposalId: null };
        };

        const proposalInfo = getProposalInfo();
        setProposalId(proposalInfo.proposalId);
        console.log('Current Proposal Info:', proposalInfo);
    }, []);

    // Fetch dropdown options
    useEffect(() => {
        const fetchActionTypes = async () => {
            try {
                const response = await apiService.getLandReportActions();
                console.log('Action Types Response:', response);

                if (response.data && Array.isArray(response.data)) {
                    const activeActions = response.data
                        .filter(action => action.is_active)
                        .map(action => ({
                            id: action.action_id,
                            type: action.action_type
                        }));
                    setRecordTypeOptions(activeActions);
                }
            } catch (error) {
                console.error('Error fetching action types:', error);
            }
        };

        fetchActionTypes();
    }, []);

    // Fetch records when projectId and proposalId are available
    useEffect(() => {
        const fetchRecords = async () => {
            if (projectId && proposalId) {
                setLoadingRecords(true);
                try {
                    await fetchProposalData();
                    await fetchTehsildarRecords();
                } catch (err) {
                    console.error('Error fetching records:', err);
                } finally {
                    setLoadingRecords(false);
                }
            }
        };

        fetchRecords();
    }, [projectId, proposalId]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const fetchTehsildarRecords = async () => {
        if (!projectId || !proposalId) {
            console.log('Invalid project or proposal ID:', { projectId, proposalId });
            return;
        }

        try {
            setLoading(true);
            const result = await apiService.getTehsildarRecord(projectId, proposalId);

            console.log('Tehsildar Records Response:', result);

            if (result.status === 200 && result.data) {
                const mappedRecords = result.data.map((item, index) => {
                    // generate a unique fallback id if report_id is missing
                    const uniqueId = item.report_id ?? `${item.owner_id ?? 'owner'}-${item.owner_survey_no || item.survey_no || 'survey'}-${index}`;

                    return {
                        id: uniqueId, // guaranteed unique per-row
                        apiId: item.report_id ?? null,
                        gatKramank: item.owner_survey_no || item.survey_no || '',
                        recordType: item.action_type || '',
                        actionId: item.action_id ?? '',
                        ferfarKramank: item.modification_no || '',
                        ferfarDinak: item.modification_date ? formatDate(item.modification_date) : '',
                        ownerId: item.owner_id || item.land_owner_id || '',
                        ownerName: item.owner_name || '',
                        isEditing: false
                    };
                });

                setRecords(mappedRecords);

                // Set work summary & report as before
                if (result.workSummary && Array.isArray(result.workSummary)) {
                    setWorkSummary(result.workSummary);
                }
                if (result.report && Array.isArray(result.report)) {
                    setReportData(result.report);
                }
            }
        } catch (error) {
            console.error('Error fetching Tehsildar records:', error);
        } finally {
            setLoading(false);
        }
    };


    const fetchProposalData = async () => {
        if (!projectId || !proposalId) {
            console.log('Invalid project or proposal ID:', { projectId, proposalId });
            return;
        }

        try {
            const result = await apiService.getProposalPublish(projectId, proposalId);

            console.log('Proposal Data Response:', result);

            if (result.status === 200 && result.data) {
                const item = result.data;

                setFormData(prev => ({
                    ...prev,
                    bhusampadan_no: item.section_11_bhusampadan_no || item.acquisition_proposal_no,
                    village_name: item.village_name || prev.village_name,
                    taluka: item.taluka_name || prev.taluka,
                    district: item.dist_name || prev.district
                }));
            }
        } catch (error) {
            console.error('Error fetching proposal data:', error);
        }
    };

    const handleEdit = (id) => {
        setRecords(prev => {
            const rec = prev.find(r => r.id === id);
            if (rec) {
                setTempData(prevTemp => ({
                    ...prevTemp,
                    [id]: {
                        gatKramank: rec.gatKramank,
                        recordType: rec.recordType,
                        actionId: rec.actionId,
                        ferfarKramank: rec.ferfarKramank,
                        ferfarDinak: rec.ferfarDinak
                    }
                }));
            }
            return prev.map(r => r.id === id ? { ...r, isEditing: true } : r);
        });
    };


    const handleSave = async (id) => {
        const record = records.find(r => r.id === id);

        if (!projectId || !proposalId) {
            alert('प्रकल्प किंवा प्रस्ताव ID उपलब्ध नाही.');
            return;
        }

        try {
            setLoading(true);

            // Convert DD.MM.YYYY → YYYY-MM-DD
            let modificationDate = '';
            if (record.ferfarDinak && record.ferfarDinak !== 'निरंक') {
                const parts = record.ferfarDinak.split('/');
                if (parts.length === 3) {
                    modificationDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
                }
            }

            const formData = new FormData();
            formData.append('project_id', projectId);
            formData.append('proposal_id', proposalId);
            formData.append('owner_id', record.ownerId || '');
            formData.append('survey_no', record.gatKramank);
            formData.append('action_id', record.actionId || tempData[id]?.actionId || recordTypeOptions[0]?.id || '');
            formData.append('modification_no', record.ferfarKramank || '');
            formData.append('modification_date', modificationDate);

            const result = await apiService.postAddUpdateTehsildarRecord(formData);

            const isSuccess = !result ||
                result.status === 200 ||
                result.status === 201 ||
                result.status === 'success' ||
                result.success === true ||
                result.data?.success === true ||
                result.data?.status === 200 ||
                result.data?.status === 201 ||
                result.message?.includes('success') ||
                result.data?.message?.includes('success');

            if (isSuccess) {
                alert('नोंद यशस्वीरित्या जतन झाली');
                setRecords(prev => prev.map(r => r.id === id ? { ...r, isEditing: false } : r));
                setTempData(prev => {
                    const updated = { ...prev };
                    delete updated[id];
                    return updated;
                });
                await fetchTehsildarRecords();
            } else {
                console.warn('Unexpected response structure:', result);
                alert('नोंद जतन करण्यात अयशस्वी.');
            }
        } catch (err) {
            const errorStatus = err.response?.status;
            const errorData = err.response?.data;

            if (errorStatus === 200 || errorStatus === 201 ||
                errorData?.status === 200 || errorData?.message?.includes('success')) {
                alert('नोंद यशस्वीरित्या जतन झाली');
                setRecords(prev => prev.map(r => r.id === id ? { ...r, isEditing: false } : r));
                setTempData(prev => {
                    const updated = { ...prev };
                    delete updated[id];
                    return updated;
                });
                await fetchTehsildarRecords();
            } else {
                alert('नोंद जतन करताना त्रुटी.');
            }
        } finally {
            setLoading(false);
        }
    };


    const handleCancel = (id) => {
        const originalData = tempData[id];
        if (originalData) {
            setRecords(prev => prev.map(r =>
                r.id === id ? {
                    ...r,
                    gatKramank: originalData.gatKramank,
                    recordType: originalData.recordType,
                    actionId: originalData.actionId,
                    ferfarKramank: originalData.ferfarKramank,
                    ferfarDinak: originalData.ferfarDinak,
                    isEditing: false
                } : r
            ));
        } else {
            // If no temp backup, simply turn off editing for that row
            setRecords(prev => prev.map(r => r.id === id ? { ...r, isEditing: false } : r));
        }

        setTempData(prev => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
        });
    };


    const handleRecordTypeChange = (id, actionId) => {
        const selectedAction = recordTypeOptions.find(opt => opt.id === parseInt(actionId));
        setRecords(records.map(record =>
            record.id === id ? {
                ...record,
                actionId: parseInt(actionId),
                recordType: selectedAction ? selectedAction.type : ''
            } : record
        ));
    };

    const handleFerfarKramankChange = (id, value) => {
        setRecords(records.map(record =>
            record.id === id ? { ...record, ferfarKramank: value } : record
        ));
    };

    const handleFerfarDinakChange = (id, value) => {
        setRecords(records.map(record =>
            record.id === id ? { ...record, ferfarDinak: value } : record
        ));
    };

    const handlePrint = () => {
        const printContent = documentRef.current?.innerHTML;
        if (!printContent) {
            alert("प्रिंट करण्यासाठी सामग्री उपलब्ध नाही.");
            return;
        }

        const win = window.open("", "", "height=900,width=1200");
        win.document.write(`
      <html>
        <head>
          <title>अहवाल - भूसंपादन प्रकरण</title>
          <style>
            @page {
          size: A4 portrait;   
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
            
            body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
              font-size: 14px;
              line-height: 1.4;
            }

            .document-container {
              max-width: 100%;
              margin: 0 auto;
            }

            .header-section {
              text-align: center;
              margin-bottom: 20px;
            }

            .header-title {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }

            .info-line {
              margin: 5px 0;
              font-size: 14px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }

            th, td {
              border: 2px solid black;
              padding: 8px;
              text-align: center;
            }

            th {
              background-color: #f3f4f6;
              font-weight: bold;
            }

            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .mb-4 { margin-bottom: 1rem; }
            .mt-8 { margin-top: 2rem; }

            .signature-section {
            display: flex;
            justify-content: space-between; 
            margin-top: 40px;
            padding: 0 40px;
          }

          .signature-box {
            text-align: center;
            flex: 1;
          }

            .page-break {
              page-break-after: always;
            }

            .page-two {
              margin-top: 40px;
            }

            .page-two-header {
              text-align: center;
              font-weight: bold;
              margin-bottom: 20px;
              font-size: 14px;
            }

            .page-two-title {
              margin-bottom: 10px;
            }

            .page-two-subtitle {
              font-size: 12px;
              margin-bottom: 15px;
            }

            .page-two table td {
              text-align: left;
              padding: 10px;
            }

            .page-two table td:first-child {
              text-align: center;
              font-weight: bold;
              width: 60px;
            }

            .page-two table td:last-child {
              text-align: center;
              width: 120px;
            }

            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);

        win.document.close();
        win.print();
    };

    // Calculate summary from workSummary data
    const totalGat = workSummary.reduce((sum, item) => sum + item.total_count, 0);
    const completedGat = workSummary.reduce((sum, item) => sum + item.completed, 0);
    const remainingGat = workSummary.reduce((sum, item) => sum + item.remaining, 0);

    // Loader display
    if (loadingRecords) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex justify-center items-center h-80">
                    <div className="text-center animate-float">
                        <div className="inline-flex relative">
                            <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
                            <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <FileText className="h-6 w-6 text-purple-400 animate-pulse" />
                            </div>
                        </div>
                        <p className="text-gray-400 mt-4">नोंदी लोड करत आहे...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-7xl mx-auto bg-white shadow-lg rounded-lg p-8">
                {/* Header with Action Buttons */}
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">भूमि अभिलेख अद्यावतीकरण</h1>
                    <div className="flex gap-3">
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
                        >
                            <Printer size={20} />
                            प्रिंट करा
                        </button>
                    </div>
                </div>

                {/* Main Table */}
                <div className="mb-8 overflow-x-auto">
                    <table className="w-full border-2 border-black">
                        <thead>
                            <tr className="bg-gray-100">
                                {/* {userRole === ROLES.LAO && ( */}
                                {/* Data Entry */}
                                {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                                    <th className="border-2 border-black p-3 text-center">कृती</th>
                                )}
                                <th className="border-2 border-black p-3 text-center">अ.क्र</th>
                                <th className="border-2 border-black p-3 text-center">
                                    कलम 11(1) खालील अधिसूचनेतील गट क्रमांक
                                </th>
                                <th className="border-2 border-black p-3 text-center">
                                    नियम 2014 चे नियम 10(3) नुसार अद्यावत करण्यात आलेल्या नोंदीचा प्रकार
                                </th>
                                <th className="border-2 border-black p-3 text-center">
                                    गाव नमुना 6 मधील फेरफार क्रमांक
                                </th>
                                <th className="border-2 border-black p-3 text-center">
                                    गाव नमुना 6 मधील फेरफार दिनांक
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="border-2 border-black p-4 text-center text-gray-500">
                                        कोणतीही नोंद उपलब्ध नाही
                                    </td>
                                </tr>
                            ) : (
                                records.map((record, index) => (
                                    <tr key={`${record.id}-${index}`}>

                                        {/* {userRole === ROLES.LAO && ( */}
                                        {/* Data Entry */}
                                        {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                                            <td className="border-2 border-black p-2">
                                                <div className="flex gap-2 justify-center">
                                                    {record.isEditing ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleSave(record.id)}
                                                                disabled={loading}
                                                                className="flex items-center gap-1 bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                                                            >
                                                                <Save size={16} />
                                                                {loading ? '' : ''}
                                                            </button>
                                                            <button
                                                                onClick={() => handleCancel(record.id)}
                                                                className="flex items-center gap-1 bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 transition-colors text-sm"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => handleEdit(record.id)}
                                                                className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors text-sm"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        <td className="border-2 border-black p-3 text-center font-semibold">
                                            {index + 1}
                                        </td>
                                        <td className="border-2 border-black p-2">
                                            <div className="p-2 text-center">{record.gatKramank}</div>
                                        </td>
                                        <td className="border-2 border-black p-2">
                                            {record.isEditing ? (
                                                <select
                                                    value={record.actionId || recordTypeOptions[0]?.id || ""}
                                                    onChange={(e) => handleRecordTypeChange(record.id, e.target.value)}
                                                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                >
                                                    {recordTypeOptions.map((option) => (
                                                        <option key={option.id} value={option.id}>
                                                            {option.type}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className="p-2">{record.recordType}</div>
                                            )}
                                        </td>
                                        <td className="border-2 border-black p-2">
                                            {record.isEditing ? (
                                                <input
                                                    type="text"
                                                    value={record.ferfarKramank}
                                                    onChange={(e) => handleFerfarKramankChange(record.id, e.target.value)}
                                                    className="w-full p-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="क्रमांक"
                                                />
                                            ) : (
                                                <div className="p-2 text-center">{record.ferfarKramank}</div>
                                            )}
                                        </td>
                                        <td className="border-2 border-black p-2">
                                            {record.isEditing ? (
                                                <input
                                                    type="date"
                                                    value={record.ferfarDinak === 'निरंक' ? '' : record.ferfarDinak.split('/').reverse().join('-')}
                                                    onChange={(e) => {
                                                        const dateValue = e.target.value;
                                                        const formattedDate = dateValue ? dateValue.split('-').reverse().join('/') : '';
                                                        handleFerfarDinakChange(record.id, formattedDate);
                                                    }}
                                                    className="w-full p-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            ) : (
                                                <div className="p-2 text-center">{record.ferfarDinak}</div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Work Details Section */}
                <div className="mb-4">
                    <h2 className="text-xl font-bold text-center mb-6">कामाचा तपशिल</h2>
                </div>

                {/* Summary Table */}
                <div className="overflow-x-auto">
                    <table className="w-full border-2 border-black">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border-2 border-black p-3 text-center">
                                    <div>कलम 11(1) खालील अधिसुचनेतील</div>
                                    <div>एकूण गट क्रमांक</div>
                                </th>
                                <th className="border-2 border-black p-3 text-center">
                                    <div>अद्यावतीकरण झालेले गट</div>
                                    <div>क्रमांक</div>
                                </th>
                                <th className="border-2 border-black p-3 text-center">
                                    <div>अद्यावतीकरण प्रलंबित असलेले गट</div>
                                    <div>क्रमांक</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="border-2 border-black p-3 text-center text-lg font-semibold">
                                    {totalGat}
                                </td>
                                <td className="border-2 border-black p-3 text-center text-lg font-semibold">
                                    {completedGat}
                                </td>
                                <td className="border-2 border-black p-3 text-center text-lg font-semibold">
                                    {remainingGat}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Hidden Print Content */}
                <div style={{ display: 'none' }}>
                    <div ref={documentRef} className="document-container">
                        {/* Header */}
                        <div className="header-section">
                            <div className="header-title">अहवाल</div>
                            <div className="info-line">
                                <strong>भूसंपादन प्रकरण क्रमांक :-</strong> {formData.bhusampadan_no || ''}
                            </div>
                            <div className="info-line">
                                <strong>गावाचे नाव :-</strong> {formData.village_name || ''}
                                <strong style={{ marginLeft: '20px' }}>तालुका :-</strong> {formData.taluka || ''}
                                <strong style={{ marginLeft: '20px' }}>जिल्हा :-</strong> {formData.district}
                            </div>
                        </div>

                        {/* Main Table */}
                        <table>
                            <thead>
                                <tr>
                                    <th>अ.क्र</th>
                                    <th>कलम 11(1) खालील अधिसूचनेतील गट क्रमांक</th>
                                    <th>नियम 2014 चे नियम 10(3) नुसार अद्यावत करण्यात आलेल्या नोंदीचा प्रकार </th>
                                    <th>गाव नमुना 6 मधील फेरफार क्रमांक</th>
                                    <th>गाव नमुना 6 मधील फेरफार दिनांक</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((record, index) => (
                                    <tr key={record.id}>
                                        <td>{index + 1}</td>
                                        <td>{record.gatKramank}</td>
                                        <td>{record.recordType}</td>
                                        <td>{record.ferfarKramank}</td>
                                        <td>{record.ferfarDinak}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Signature Section */}
                        <div className="signature-section">
                            <div className="signature-box">
                                <div className="font-bold">ग्राम महसूल अधिकारी</div>
                            </div>

                            <div className="signature-box">
                                <div className="font-bold">तहसीलदार</div>
                            </div>

                            <div className="signature-box">
                                <div className="font-bold">मंडळ अधिकारी</div>
                            </div>
                        </div>

                        {/* Page Break */}
                        <div className="page-break"></div>

                        {/* Page 2 - Report page with dynamic data */}
                        <div className="page-two">
                            <div className="page-two-header">
                                <div className="page-two-title">
                                    संपादनाखालील जमिनीचे भूमि अभिलेख अद्यावतीकरण कार्यपुर्ती अनुपालन अहवाल
                                </div>
                                <div className="page-two-subtitle">
                                    (पहा-भूमिसंपादन,पुनर्वसन व पुनर्स्थापना करतांना वाजवी भरपाई मिळण्याचा व पारपर्शकतेचा हक्क<br />
                                    (महाराष्ट्र) नियम 2014 मधील नियम 10(3))
                                </div>
                                <div style={{ marginBottom: '15px', fontSize: '12px' }}>
                                    <strong>भूसंपादन प्रकरण क्रमांक :- {formData.bhusampadan_no || ''}</strong><br />
                                    <strong>गावाचे नाव :- {formData.village_name || ''}</strong> <strong>तालुका :- {formData.taluka || ''}</strong> <strong>जिल्हा :- {formData.district}</strong>
                                </div>
                            </div>

                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>अ.क्र</th>
                                        <th>पूर्ण केलेल्या कार्याबाबत तपशिल</th>
                                        <th style={{ width: '120px' }}>गट क्रमांक</th>
                                        <th style={{ width: '120px' }}>नोंदीची संख्या.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.action_id}</td>
                                            <td>{item.action_type}</td>
                                            <td>{item.survey_no}</td>
                                            <td>{item.count}</td>
                                        </tr>
                                    ))}
                                    {/* Fill remaining rows up to 18 */}
                                    {[...Array(Math.max(0, 18 - reportData.length))].map((_, idx) => (
                                        <tr key={`empty-${idx}`}>
                                            <td>{reportData.length + idx + 1}</td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Page 2 Signature Section */}
                            <div className="signature-section">
                                <div className="signature-box">
                                    <div className="font-bold">ग्राम महसूल अधिकारी</div>
                                </div>

                                <div className="signature-box">
                                    <div className="font-bold">तहसीलदार</div>
                                </div>

                                <div className="signature-box">
                                    <div className="font-bold">मंडळ अधिकारी</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HighwayTehsildar;